import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Plus, ClipboardText, Users, ChartBar, Trash, ArrowRight, Eye, GearSix
} from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/assessments`, { withCredentials: true }),
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
      ]);
      setAssessments(aRes.data);
      setStats(sRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try {
      await axios.delete(`${API}/assessments/${id}`, { withCredentials: true });
      toast.success("Assessment deleted");
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  const statusColor = (s) => {
    if (s === "published") return "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20";
    if (s === "closed") return "bg-[#52525B]/10 text-[#52525B] border-[#52525B]/20";
    return "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-14 bg-white" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0A0A]">
              Dashboard
            </h1>
            <p className="text-sm text-[#52525B] mt-1">
              Welcome back, {user?.name?.split(" ")[0]}
            </p>
          </div>
          <Button
            onClick={() => navigate("/create-assessment")}
            className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-5 py-2.5 h-auto font-semibold text-sm"
            data-testid="create-assessment-btn"
          >
            <Plus size={16} className="mr-1.5" />
            New Assessment
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-black/10 mb-8" data-testid="dashboard-stats">
            {[
              { label: "Assessments", value: stats.total_assessments, icon: ClipboardText },
              { label: "Candidates", value: stats.total_candidates, icon: Users },
              { label: "Completed", value: stats.total_completed, icon: ChartBar },
              { label: "Avg Score", value: `${stats.average_score}%`, icon: ChartBar },
            ].map((s, i) => (
              <div key={i} className="p-5 border-r last:border-r-0 border-black/10">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon size={16} className="text-[#52525B]" />
                  <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">{s.label}</span>
                </div>
                <p className="font-heading font-black text-2xl text-[#0A0A0A] tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Assessment List */}
        {assessments.length === 0 ? (
          <div className="border border-black/10 p-12 text-center" data-testid="empty-state">
            <ClipboardText size={40} className="text-[#52525B]/30 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-[#0A0A0A] mb-2">No assessments yet</h3>
            <p className="text-sm text-[#52525B] mb-6">Create your first AI-powered assessment from a job description.</p>
            <Button
              onClick={() => navigate("/create-assessment")}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-6 py-2.5 h-auto font-semibold text-sm"
              data-testid="create-first-btn"
            >
              <Plus size={16} className="mr-1.5" />
              Create Assessment
            </Button>
          </div>
        ) : (
          <div className="border border-black/10" data-testid="assessment-list">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-black/10 bg-[#F8F9FA]">
              <span className="col-span-5 text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Assessment</span>
              <span className="col-span-2 text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Status</span>
              <span className="col-span-1 text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Qs</span>
              <span className="col-span-2 text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Candidates</span>
              <span className="col-span-2 text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] text-right">Actions</span>
            </div>
            {assessments.map((a) => (
              <div
                key={a.assessment_id}
                className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-black/5 hover:bg-[#F8F9FA] transition-colors items-center"
                data-testid={`assessment-row-${a.assessment_id}`}
              >
                <div className="col-span-5">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{a.title}</p>
                  <p className="text-xs text-[#52525B] mt-0.5">
                    {new Date(a.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="col-span-2">
                  <span className={`inline-block text-[10px] px-2 py-0.5 font-bold uppercase border ${statusColor(a.status)}`}>
                    {a.status}
                  </span>
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-sm font-medium text-[#0A0A0A] tabular-nums">{a.question_count}</span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="text-sm font-medium text-[#0A0A0A] tabular-nums">
                    {a.completed_count || 0}/{a.candidate_count}
                  </span>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => navigate(`/assessments/${a.assessment_id}/results`)}
                        className="p-1.5 hover:bg-black/5 transition-colors"
                        data-testid={`view-results-${a.assessment_id}`}
                      >
                        <Eye size={16} className="text-[#52525B]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">View results</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => navigate(`/assessments/${a.assessment_id}/invite`)}
                        className="p-1.5 hover:bg-black/5 transition-colors"
                        data-testid={`invite-btn-${a.assessment_id}`}
                      >
                        <Users size={16} className="text-[#52525B]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Invite candidates</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => navigate(`/assessments/${a.assessment_id}/settings`)}
                        className="p-1.5 hover:bg-black/5 transition-colors"
                        data-testid={`settings-btn-${a.assessment_id}`}
                      >
                        <GearSix size={16} className="text-[#52525B]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Settings</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleDelete(a.assessment_id)}
                        className="p-1.5 hover:bg-[#EF4444]/10 transition-colors"
                        data-testid={`delete-btn-${a.assessment_id}`}
                      >
                        <Trash size={16} className="text-[#EF4444]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Delete</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
